import React, { createContext, useState, useContext, useEffect, useRef } from 'react';
import { ordersAPI, trackingAPI } from '../services/api';

const CartContext = createContext();

export const useCart = () => {
  const context = useContext(CartContext);
  if (!context) {
    throw new Error('useCart must be used within a CartProvider');
  }
  return context;
};

export const CartProvider = ({ children }) => {
  const [cart, setCart] = useState([]);
  const syncTimeout = useRef(null);

  useEffect(() => {
    const storedCart = localStorage.getItem('zl_cart');
    if (storedCart) {
      setCart(JSON.parse(storedCart));
    }
  }, []);

  useEffect(() => {
    localStorage.setItem('zl_cart', JSON.stringify(cart));

    // Debounced sync to backend for abandoned-cart tracking
    if (syncTimeout.current) clearTimeout(syncTimeout.current);
    syncTimeout.current = setTimeout(() => {
      const token = localStorage.getItem('zl_token');
      if (token && cart.length > 0) {
        const total = cart.reduce((t, i) => t + i.price * i.quantity, 0);
        ordersAPI.syncCart(cart, total).catch(() => {});
      }
    }, 2000);
  }, [cart]);

  const addToCart = (product, quantity = 1, variant = null) => {
    const productId = product._id || product.id;
    const key = variant?.sku ? `${productId}__${variant.sku}` : productId;
    setCart(prevCart => {
      const existingItem = prevCart.find(item => (item.key || item.id) === key);
      if (existingItem) {
        return prevCart.map(item =>
          (item.key || item.id) === key
            ? { ...item, quantity: item.quantity + quantity }
            : item
        );
      }
      const newItem = {
        key,
        id: productId,
        title: product.title,
        price: variant ? Number(variant.price) : product.price,
        image: product.images?.[0] || '',
        seller: product.seller,
        seller_id: product.seller_id,
        stock: variant ? Number(variant.stock) : product.stock,
        variant_sku: variant?.sku || null,
        variant_label: variant ? [variant.size, variant.color].filter(Boolean).join(' / ') || variant.variant_value : null,
        freeShipping: product.free_shipping,
        quantity
      };
      return [...prevCart, newItem];
    });

    // GA4 add_to_cart event
    if (typeof window.gtag === 'function') {
      window.gtag('event', 'add_to_cart', {
        currency: 'MXN',
        value: product.price * quantity,
        items: [{
          item_id: product._id,
          item_name: product.title,
          price: product.price,
          quantity
        }]
      });
    }

    // Session tracking
    trackingAPI.logEvent('add_to_cart', {
      product_id: product._id,
      title: product.title,
      price: product.price,
      quantity,
    });
  };

  const removeFromCart = (productId) => {
    setCart(prevCart => prevCart.filter(item => (item.key || item.id) !== productId));
  };

  const updateQuantity = (productId, quantity) => {
    if (quantity <= 0) {
      removeFromCart(productId);
      return;
    }
    setCart(prevCart =>
      prevCart.map(item =>
        (item.key || item.id) === productId ? { ...item, quantity } : item
      )
    );
  };

  const clearCart = () => {
    setCart([]);
  };

  const getCartTotal = () => {
    return cart.reduce((total, item) => total + item.price * item.quantity, 0);
  };

  const getCartCount = () => {
    return cart.reduce((count, item) => count + item.quantity, 0);
  };

  return (
    <CartContext.Provider
      value={{
        cart,
        addToCart,
        removeFromCart,
        updateQuantity,
        clearCart,
        getCartTotal,
        getCartCount
      }}
    >
      {children}
    </CartContext.Provider>
  );
};
